<template>
  <div>
    <form-text-box
      ref="rentInfoBox"
      :dataList="rentInfo_formConfig"
      :rules="rentInfo_formRule"
      :model="rentInfo_model"
      :column="2"
    />
  </div>
</template>
<script>
import { getLeasingSchemeConfigListAll } from '@/api/domain/cars'
import { getRentPlanConfig } from '@/api/domain/orders.js'
import {
  FuzzySearch,
} from '@/api/purchaseAndSaleAPI'
import { CATEGORY_CODE_MAP, CATEGORY_FEE_MAP, FEE_CODE_MAP, PAYMODE_CODE_MAP } from '@/store/dict/rentPlanDict.js'
import { formatNumberWithCommas } from '@/utils'
import * as Sentry from '@sentry/vue'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { mapState } from 'vuex'
import { rentInfoFn } from './config.js'
import Context, { CONTEXT_TYPE } from './context/index.js'

export default {
  name: 'PersonalRentInfo',
  inject: ['getOrderConfigInfo'],
  props: {
    isEnterChildOrder: { // 企业子订单
      type: Boolean,
      default: false,
    },
    defaultRentInfo: {
      type: Object,
      default: () => ({
        rentCategory: '', // 类别
        signDate: '', // 签署日期
        startStopDate: [], // 收款起止日期
        presentDays: '', // 赠送天数
        preFreeRentDays: '', // 前置免租天数
        isTranDeposit: '', // 是否平移原订单押金 1-是 0-否
        modelId: '',
        planId: null, // 租赁方案
      }),
    },
    showDeposit: {
      type: Boolean,
      default: false,
    },
    planId: {
      type: [String, Number],
      default: '',
    },
    forbiddenEndDate: {
      type: String,
      default: '',
    },
    deposit: {
      type: Object,
      default: () => ({
        depositAmount: '', // 押金应收
        depositReceived: '', // 已收押金
        flag: '',
        reason: '',
      }),
    },
  },
  data() {
    const { rentInfo_model, rentInfo_formRule } = rentInfoFn.call(this)
    return {
      rentInfo_model,
      rentInfo_formRule,
      feeTableData: [],
      rentPlanPeriod: '', // 选择租赁方案的预计租赁周期
      rentPlanPeriodType: null, // 选择租赁方案的周期类型，1：天、3：月
      relationOrderType: 0,
      rentPlanList: [{ id: 0, name: '自定义' }],
      plateNoList: [],
      yaJinObj: {
        fundType: FEE_CODE_MAP.YA_JIN,
        isConfirm: false,
        amount: this.deposit.depositAmount || '',
        payMode: PAYMODE_CODE_MAP.DISPOSABLE, // 一次性
        isSelectScheme: true,
        advancePayment: 2, // 发车前收款：2-收取全部
        items: [
          {
            dueDate: '',
            endCycleDate: '',
            planAmount: this.deposit.depositAmount || '',
            remarks: '',
            startCycleDate: '',
          },
        ],
      },
      rentPlanListLoading: false,
    }
  },
  watch: {
    defaultRentInfo: {
      handler(val) {
        const { signDate, startStopDate, presentDays, preFreeRentDays, rentCategory, isTranDeposit, modelId, carId, plateNo, planId, relatedOrderId } = val
        this.rentInfo_model.signDate = signDate || new Date()
        this.rentInfo_model.startStopDate = startStopDate
        this.rentInfo_model.presentDays = presentDays
        this.rentInfo_model.preFreeRentDays = preFreeRentDays
        this.rentInfo_model.rentCategory = rentCategory
        this.rentInfo_model.isTranDeposit = isTranDeposit
        this.rentInfo_model.modelId = modelId
        this.rentInfo_model.carId = carId
        this.rentInfo_model.planId = planId
        this.rentInfo_model.relatedOrderId = relatedOrderId

        // 如果是租赁商城单，需要手动获取车型和租赁方案
        if (Number(this.$route.query.source) === 2) {
          // 获取租赁方案
          this.getRentPlanList()
          // 获取租赁方案详情
          if (this.planId) {
            this.getPlanDetail({ planId: this.planId })
          }
        }

        // 获取车牌列表
        this.getPlateNoData(plateNo)
      },
      deep: true,
      immediate: true,
    },
    'defaultRentInfo.rentCategory': {
      handler(val) {
        if (val) {
          this.queryFeeList().then((feeList) => this.addRequiredFee(feeList))
          // 获取租赁方案
          this.getRentPlanList()
        }
      },
    },
    'rentInfo_model.startStopDate': {
      handler(val) {
        if (!val) return
        this.calculateContractDate()
      },
      immediate: true,
    },
    'rentInfo_model.presentDays': {
      handler(val) {
        if (!val) return
        this.calculateContractDate()
      },
    },
    'rentInfo_model.planId': {
      handler(val) {
        this.$emit('plantIdChange', val)
      },
    },
    disableDeposit: {
      handler(yes) {
        // 如果禁用平移押金选项，则一定是不平移押金，如果不禁用平移押金选项，则可以选是或否
        if (yes) this.rentInfo_model.isTranDeposit = 0
      },
      immediate: true,
    },
    'rentInfo_model.isTranDeposit': {
      handler(val) {
        if (!this.showDeposit) return
        this.onTranDepositChange(val)
      },
      immediate: true,
    },
    'rentInfo_model.rentCategory': {
      handler(val) {
        if (val) {
          // 租赁类型改变时，重新获取该类型下的租赁方案
          this.getRentPlanList()
          this.queryFeeList().then((feeList) => this.addRequiredFee(feeList))
        }
      },
    },
    planId: {
      handler(val) {
        if (val) {
          this.rentInfo_model.planId = val
          if (val) {
            this.getPlanDetail({ planId: val })
          }
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState({
      categoryList: (state) => state.rentPlan.categoryList, // 类别
      orderSignRentFee: (state) => state.rentPlan.orderSignRentFee, // 租车费项
      orderSignPurchaseFee: (state) => state.rentPlan.orderSignPurchaseFee, // 已租代购费项
      orderSignCooperateFee: (state) => state.rentPlan.orderSignCooperateFee, // 司机合作模式费项
    }),
    // @reference: ./context/index.js
    context() {
      const contextName = this.showDeposit ? CONTEXT_TYPE.ORDER_RENEWAL : CONTEXT_TYPE.DEFAULT
      return Context.get(contextName, this)
    },
    disableDeposit() {
      // 以租代购或者司机合作模式，无论是否原单有押金，均不可选择平移押金
      const { SI_JI_HE_ZUO_MO_SHI, YI_ZU_DAI_GOU } = CATEGORY_CODE_MAP
      const { rentCategory } = this.rentInfo_model
      if ([YI_ZU_DAI_GOU, SI_JI_HE_ZUO_MO_SHI].includes(rentCategory)) {
        return true
      }
      return !this.deposit.flag
    },
    rentInfo_formConfig() {
      const { rentInfo_formConfig } = rentInfoFn.call(this)
      return rentInfo_formConfig
    },
    categoryFilterList() {
      // 转租赁模式:类别只展示租车
      if (this.relationOrderType === 5) {
        return this.categoryList.filter((i) => i.code === CATEGORY_CODE_MAP.ZU_CHE)
      }
      // 企业子订单，只展示租车和以租代购
      if (this.isEnterChildOrder) {
        return this.categoryList.filter((i) => i.code !== CATEGORY_CODE_MAP.SI_JI_HE_ZUO_MO_SHI)
      }
      return this.categoryList
    },
    /*
      签署日期选择控件的配置项
      签署日期应小于等于收款的开始时间
    */
    signInfoScrapDate() {
      return {
        disabledDate: (time) => {
          const { startStopDate } = this.rentInfo_model
          if (!startStopDate || !startStopDate.length) {
            // 收款起始日期还没选择的情况下，签署日期控件不受限制
            return false
          }
          return moment(time.getTime()).isAfter(moment(startStopDate[0]))
        },
      }
    },
    /*
      收款起止日期选择控件的配置项
      收款起止日期应大于等于签署日期
    */
    startStopInfoScrapDate() {
      if (this.showDeposit || this.isEnterChildOrder) {
        return {
          disabledDate: (time) => {
            const prevOrderEndDate = this.forbiddenEndDate
            return moment(time.getTime()).isBefore(moment(prevOrderEndDate).add(1, 'days').format('yyyy-MM-DD'))
          },
          onPick: this.onPick,
        }
      }
      return {
        onPick: this.onPick,
      }
    },
    /*
      下一期最近可用的收款"起始"日期字符串，格式为 yyyy-MM-dd
      因为 上一期的结束日期 < 签署日期 <= 下一期最近可用的收款起始日期
      因此 下一期最近的可用收款起始日期 = max(签署日期, 上一期的结束日期 + 1天)
      如果签署日期和上一期的结束日期都为空，则返回new Date()
    */
    availablePaymentStartDate() {
      // 签署日期（通常，签署日期会在页面加载时选择会当天，所以一般不会为空）
      const { signDate } = this.rentInfo_model
      // 上一期订单的结束日期（上一期的结束日期可能为空，例如在新建订单时），上一期的结束日期包含赠送天数
      const prevOrderEndDate = this.forbiddenEndDate
      if (!signDate && !prevOrderEndDate) {
        return moment().format('yyyy-MM-DD')
      }

      if (!signDate) {
        return moment(prevOrderEndDate).add(1, 'days').format('yyyy-MM-DD')
      }

      if (!prevOrderEndDate) {
        return moment(signDate).format('yyyy-MM-DD')
      }
      // signDate默认为当天，默认取合同签署日期+1天
      return moment(prevOrderEndDate).add(1, 'days').format('yyyy-MM-DD')
    },
    // 订单配置信息
    orderConfigInfo() {
      return (this.getOrderConfigInfo && this.getOrderConfigInfo()) || {}
    },
  },
  mounted() {
    // 获取类别list
    if (this.categoryList === null || !this.categoryList.length) {
      this.$store.dispatch('getRentCategory').then(() => {
        this.setCateGoryFn()
      })
    } else {
      this.setCateGoryFn()
    }

    this.context.onMounted()

    // relationOrderType 1-临时换车，2-订单换车，3-订单换人，4-订单续约 5-转租赁
    const { relationOrderType = 0 } = this.$route.query
    this.relationOrderType = Number(relationOrderType)

    if (isEmpty(this.orderConfigInfo)) {
      this.queryFeeList().then((feeList) => this.addRequiredFee(feeList))
      return
    }
    const someFeeList = this[CATEGORY_FEE_MAP[this.orderConfigInfo.rentCategory]]
    if (!someFeeList || !someFeeList.length) {
      this.queryFeeList().then((feeList) =>
        this.setFeeItemArr(feeList, this.orderConfigInfo.orderAccruedPlans))
    } else {
      this.setFeeItemArr(someFeeList, this.orderConfigInfo.orderAccruedPlans)
    }
  },
  methods: {
    // 选择租赁方案后 结束日期=开始日期+预租周期
    onPick(time) {
      if (!this.planId) return
      this.$nextTick(async () => {
        this.context.onPick(time)
      })
      // 关闭--时间控件下拉框
      this.$refs.rentInfoBox
        .getRefs('startStopDate')
        .$children[1].$children[0].handleClose()
    },
    onPlanChange(planId) {
      this.context.onPlanChange(planId)
    },
    // 所有租赁信息清空
    resetFormData() {
      this.rentInfo_model.rentCategory = CATEGORY_CODE_MAP.ZU_CHE
      this.rentInfo_model.startStopDate = []
      this.rentInfo_model.presentDays = ''
      this.rentInfo_model.preFreeRentDays = ''
      this.rentPlanPeriod = ''
      this.rentPlanPeriodType = null
      this.queryFeeList().then((feeList) => this.addRequiredFee(feeList, 'fromEvent'))
      this.$nextTick(() => {
        this.$refs.rentInfoBox.clearValidate()
      })
    },
    // 设置租赁类别默认值
    setCateGoryFn() {
      if (this.defaultRentInfo.rentCategory) {
        this.rentInfo_model.rentCategory = this.defaultRentInfo.rentCategory
      } else {
        // 默认展示租车
        const someOne = this.categoryList?.find(
          (item) => Number(item.code) === CATEGORY_CODE_MAP.ZU_CHE,
        )
        if (someOne) {
          this.rentInfo_model.rentCategory = someOne.code
        }
        this.updateData()
      }
    },
    // 获取方案详情
    async getPlanDetail(params) {
      const { data = {} } = await getRentPlanConfig(params)
      this.rentInfo_model.payPlanFee = data.payPlanFee
      this.rentInfo_model.rentCategory = data.rentCategory
      this.rentInfo_model.modelId = data.modelId
      this.rentInfo_model.startStopDate = [
        moment(data.startDate).format('YYYY-MM-DD HH:mm:ss'),
        moment(data.endDate).format('YYYY-MM-DD HH:mm:ss'),
      ]
      this.rentPlanPeriod = data.rentPeriod
      this.rentPlanPeriodType = data.rentPeriodType
      this.rentInfo_model.presentDays = data.presentDays
      this.rentInfo_model.presentDaysText = `${Number(data.presentDays || 0)}天`
      this.rentInfo_model.preFreeRentDays = data.preFreeRentDays
      this.rentInfo_model.orderAccruedPlans = data.orderAccruedPlans || []

      if (!CATEGORY_FEE_MAP[data.rentCategory]) {
        Sentry.captureException('详情接口未返回租赁类别')
        return
      }
      const someFeeList = this[CATEGORY_FEE_MAP[data.rentCategory]]
      if (!someFeeList || !someFeeList.length) {
        this.queryFeeList().then((feeList) =>
          this.setFeeItemArr(feeList, data.orderAccruedPlans))
      } else {
        this.setFeeItemArr(someFeeList, data.orderAccruedPlans)
      }
    },
    setFeeItemArr(feeList, orderPlanFeeList) {
      const safeOrderPlanFeeList = Array.isArray(orderPlanFeeList) ? orderPlanFeeList : []
      const feeData = safeOrderPlanFeeList.reduce(this.context.onSetFee(feeList), [])
      this.feeTableData = feeData
      this.updateData('fromEvent')
    },
    // 获取费项list
    async queryFeeList() {
      if (!this.rentInfo_model.rentCategory) {
        this.rentInfo_model.rentCategory = CATEGORY_CODE_MAP.ZU_CHE
      }
      const someFeeList =
        this[CATEGORY_FEE_MAP[this.rentInfo_model.rentCategory]]
      if (!someFeeList || !someFeeList.length) {
        const res = await this.$store.dispatch('getFeeList', {
          type: CATEGORY_FEE_MAP[this.rentInfo_model.rentCategory],
        })
        return res.data || []
      }
      return someFeeList
    },
    addRequiredFee(someFeeList, from) {
      // 过滤必填费项
      const requiredFee = someFeeList.filter((item) => item.required) || []
      let formatFees = requiredFee.map((i) => {
        const item = {}
        item.fundType = i.code
        item.name = i.name
        item.required = i.required
        item.toast = i.toast
        item.isConfirm = false
        return item
      })
      // 若类型选的是【以租代购】,(首付款/租金)和(全款)是互斥关系，table默认展示 首付款/租金，全款需过滤掉
      if (Number(this.rentInfo_model.rentCategory) === CATEGORY_CODE_MAP.YI_ZU_DAI_GOU) {
        // 15-全款
        formatFees = formatFees.filter((i) => Number(i.fundType) !== FEE_CODE_MAP.QUAN_KUAN)
      }
      formatFees = this.context.onAddRequiredFee(formatFees)
      // 增加from ==='fromEvent'，解决租赁方案切换时费项列表不更新问题
      if (this.feeTableData.length === 0 || from === 'fromEvent') this.feeTableData = formatFees
      this.updateData(from)
    },
    onTranDepositChange(val) {
      this.context.onTranDepositChange(val)
    },
    // 计算账期天数和合同的起止日
    calculateContractDate() {
      let startDate = ''
      let endDate = ''
      let lint = ''
      let totalDays = 0
      const presentDay = this.rentInfo_model.presentDays || 0
      if (
        Array.isArray(this.rentInfo_model.startStopDate) &&
        this.rentInfo_model.startStopDate.length
      ) {
        startDate = this.rentInfo_model.startStopDate[0].split(' ')[0]
        lint = this.rentInfo_model.startStopDate[1].split(' ')[0]
        endDate = presentDay ? moment(lint).add(presentDay, 'days').format('YYYY-MM-DD') : lint

        this.rentInfo_model.contractDateText = `${startDate}~${endDate}`
        totalDays = moment(endDate).diff(moment(startDate), 'days') + 1
        const formateNum = formatNumberWithCommas(totalDays)
        this.rentInfo_model.rentPeriodText = `共 ${formateNum} 天`
      } else {
        this.rentInfo_model.contractDateText = ''
        this.rentInfo_model.rentPeriodText = ''
      }
      // 赠送天数
      this.rentInfo_model.presentDaysText = `${Number(presentDay)}天`
      // 前置免租天数
      this.rentInfo_model.preFreeRentDaysText = `${Number(this.rentInfo_model.preFreeRentDays || 0)}天`

      // 更新数据，传递到父组件
      this.updateData()
      return totalDays
    },
    updateData(from) {
      this.$emit('updateInfo', this.rentInfo_model, this.feeTableData, from)
    },
    // 获取表单数据
    getFormData() {
      let returnData = false
      this.$refs.rentInfoBox.getRefs().validate((validate) => {
        if (validate) {
          const date = new Date(this.rentInfo_model.signDate)
          date.setHours(0, 0, 0, 0)
          returnData = {
            rentCategory: Number(this.rentInfo_model.rentCategory), // 租赁类别（租车，已租代购，司机合作模式）
            signDate: moment(this.rentInfo_model.signDate).format(
              'YYYY-MM-DD 00:00:00',
            ), // 签署日期
            startStopDate: this.rentInfo_model.startStopDate, // 收款起止日期
            startDate: this.rentInfo_model.startStopDate[0],
            endDate: this.rentInfo_model.startStopDate[1],
            presentDays: Number(this.rentInfo_model.presentDays), // 赠送天数
            preFreeRentDays: Number(this.rentInfo_model.preFreeRentDays), // 前置免租天数
            isTranDeposit: this.rentInfo_model.isTranDeposit, // 是否平移原订单押金 1-是 0-否
            cycleDays: this.calculateContractDate(), // 总天数
            carId: this.rentInfo_model.carId,
            modelId: this.rentInfo_model.modelId,
            planId: this.rentInfo_model.planId,
          }
        } else {
          this.autoAnchorPoint(true)
        }
      })
      return returnData
    },
    // 锚点定位
    autoAnchorPoint(toastFlag) {
      toastFlag &&
        this.$message.error('请填写带有红色 * 的内容')
      this.$refs.rentInfoBox.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      this.$refs.rentInfoBox.getRefs().validate().catch(() => {}) // 表单校验
    },
    startStopDateChange() {
      this.calculateContractDate()
      this.updateData()
      this.$emit('manualChangeStartStopDate')
    },
    clearFormData() {
      this.context.clearFormData()
    },
    async getRentPlanList() {
      this.rentPlanListLoading = true
      const { code, data } = await getLeasingSchemeConfigListAll(
        this.context.generateGetPlanParams(),
      )
      this.rentPlanListLoading = false
      if (code === 1) {
        const rentPlanList = data || []
        this.rentPlanList = [{ id: 0, name: '自定义' }, ...rentPlanList]
      }
      this.$emit('updateRentPlanList', this.rentPlanList)
    },
    // 车牌号远程搜索
    async getPlateNoData(query) {
      if (query && query.length >= 4) {
        const { code, data } = await FuzzySearch({
          plateNo: query,
          state: this.showDeposit ? 1 : 3, // 如果是续租则使用1，否则使用3
          modelId: this.rentInfo_model.modelId,
          orderId: this.$route.query.orderId,
        })
        if (code === 1) {
          this.plateNoList = data || []
        }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep .tran-deposit-radio {
  .el-form-item__label {
    width: auto;
    min-width: 150px;
    max-width: 300px;
    white-space: pre-line;
  }
}
</style>
